import React, { useState } from "react";
import Link from "next/link";
import { PiArrowBendUpRightThin } from "react-icons/pi";
import { BsCheckCircleFill, BsFillXCircleFill } from "react-icons/bs";
import { motion } from "framer-motion";
import { Switch } from "@components/MiniComponents";
import CtaOne from "@components/CTA/CtaOne";
import FeatureInfoPopup from "@components/Cards/FeatureImagePopup";

import { BASE_ASSETS_URL } from "@constants/constants";

const PricingComparisons2 = ({
  monthly = false,
  showSwitch = true,
  heading = "Compare all the plans and choose the best one",
  paragraph = "Our price comparison table is designed to help you save money and time, ensuring you always get the best value for your purchases.",
  paragraphStyling = "max-w-[550px] mx-auto",
  stickyMargin = "lg:top-[112px]",
}) => {
  const [isMonthly, setIsMonthly] = useState(monthly);
  const [imagePopup, setImagePopup] = useState(null);
  const [activePlanIndex, setActivePlanIndex] = useState(0);

  const changeTab = () => {
    setIsMonthly(!isMonthly);
  };

  const pricingComparisonsData = {
    plans: [
      {
        name: "Freelancer",
        price: {
          symbol: "$",
          amount: isMonthly ? "15" : "10",
          duration: "/mo",
        },
        bgColor: "bg-[#f8e3f8]",
      },
      {
        name: "Team",
        price: {
          symbol: "$",
          amount: isMonthly ? "69" : "49",
          duration: "/mo",
        },
        bgColor: "bg-[#b8f6b7]",
      },
      {
        name: "Growing Business",
        price: {
          symbol: "$",
          amount: isMonthly ? "129" : "99",
          duration: "/mo",
        },
        bgColor: "bg-[#fee5cd]",
      },
    ],
    features: [
      {
        category: "Usage",
        items: [
          {
            name: "Number Of WorkSpaces",
            values: ["1 Default", "1 Default", "Unlimited & Free"],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/team-workspaces.png`,
          },
          {
            name: "Allowed Form Responses(Signature)",
            values: ["100 /month", "500 /month", "Unlimited"],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/forms.png`,
          },
          {
            name: "Number of PDFs",
            values: ["10 /month", "Unlimited", "Unlimited"],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/pdf.png`,
          },
          {
            name: "Number of Templates",
            values: ["10", "Unlimited", "Unlimited"],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/pdf-template.png`,
          },
          {
            name: "Number of Templates Responses",
            values: ["10", "Unlimited", "Unlimited"],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/pdf-template.png`,
          },
          {
            name: "Number of PDFs from Form Submissions",
            values: ["100", "Unlimited", "Unlimited"],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/generate-response-pdf.png`,
          },
        ],
      },
      {
        category: "Functionality",
        items: [
          {
            name: "Audit Log and History",
            values: [true, true, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/audit-trail.png`,
          },
          {
            name: "Signing Order",
            values: [true, true, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/signing-order.png`,
          },
          {
            name: "Form fields: drop-down, radio, etc.",
            values: [true, true, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/doc-editor.png`,
          },
          {
            name: "Notifications",
            values: [true, true, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/real-time-notifications.png`,
          },
          {
            name: "Form Branding",
            values: [true, true, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/form-branding.png`,
          },
          {
            name: "Send Generated PDFs",
            values: [true, true, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/individual-file-sharing.png`,
          },
          {
            name: "Structured Folder System",
            values: [true, true, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/create-files-folders.png`,
          },
          {
            name: "Custom PDF Name",
            values: [false, true, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/custom-pdf-name.png`,
          },
          {
            name: "Form Analytics",
            values: [false, true, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/form-analytics.png`,
          },
          {
            name: "Custom WaterMark on Generated PDFs",
            values: [false, true, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/watermark.png`,
          },
          {
            name: "Bulk Send",
            values: [false, true, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/bulk-send.png`,
          },
          {
            name: "Bulk Generated PDF Download",
            values: [false, true, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/bulk-download.png`,
          },
          {
            name: "Admin Dashboard for User Management",
            values: [false, true, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/custom-user-roles.png`,
          },
          {
            name: "Share Templates with Team Members",
            values: [false, true, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/team-workspaces.png`,
          },
          {
            name: "Signing via Shareable Link and QR Code",
            values: [false, true, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/generate-signing-link.png`,
          },
          {
            name: "Reminders",
            values: [false, false, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/reminders.png`,
          },
          {
            name: "Workspaces",
            values: [false, false, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/team-workspaces.png`,
          },
          {
            name: "Custom Email Branding",
            values: [false, false, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/email-subject-body.png`,
          },
          {
            name: "Redirection URL in Forms",
            values: [false, false, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/custom-redirection-url.png`,
          },
          {
            name: "Redirection URL in PDFs",
            values: [false, false, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/custom-redirection-url.png`,
          },
          {
            name: "Password proctected PDFs",
            values: [false, false, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/password-protection.png`,
          },
          {
            name: "Stripe",
            values: [false, false, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/stripe-integration.png`,
          },
          {
            name: "Custom Development using our API",
            values: ["Contact Us", "Contact Us", "Contact Us"],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/api-integration.png`,
          },
        ],
      },
      {
        category: "Integrations",
        items: [
          {
            name: "Google Sheets Integration",
            values: [true, true, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/google-sheet.png`,
          },
          {
            name: "Google Contacts Integration",
            values: [false, true, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/import-contacts.png`,
          },
          {
            name: "Google Drive Integration",
            values: [false, true, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/google-drive.png`,
          },
          {
            name: "Pipedrive Integration",
            values: [false, false, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/pipedrive.png`,
          },
          // {
          //   name: "Zapier Integration",
          //   values: [false, false, true],
          //   image: `${BASE_ASSETS_URL}/v2/ltd/features/zapier.png`,
          // },
          // {
          //   name: "Pabbly Integration",
          //   values: [false, false, true],
          //   image: `${BASE_ASSETS_URL}/v2/ltd/features/pabbly.png`,
          // },
          {
            name: "Dropbox Integration",
            values: [false, true, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/dropbox.png`,
          },
          {
            name: "Onedrive Integration",
            values: [false, true, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/onedrive.png`,
          },
          {
            name: "Highlevel Integration",
            values: [false, false, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/highlevel.png`,
          },
          // {
          //   name: "Make Integration",
          //   values: [false, false, true],
          //   image: `${BASE_ASSETS_URL}/v2/ltd/features/make.png`,
          // },
          {
            name: "Hubspot Integration",
            values: [false, false, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/hubspot.png`,
          },
          {
            name: "Stripe Integration",
            values: [false, false, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/stripe-integration.png`,
          },
        ],
      },
      {
        category: "Security",
        items: [
          {
            name: "HIPAA BAA",
            values: [
              "additonal 49$/mo",
              "additonal 49$/mo",
              "additonal 49$/mo",
            ],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/hipaa-compliance.png`,
          },
        ],
      },
      {
        category: "Customer Support",
        items: [
          {
            name: "Support Reply within 2 Days",
            values: [true, true, true],
            image: `${BASE_ASSETS_URL}/v2/ltd/features/chat-support.png`,
          },
        ],
      },
    ],
  };

  return (
    <div className="flex flex-col ">
      <div className="w-full relative max-w-screen-xl mx-auto px-4 md:px-12 mb-10">
        <h3 className="text-4xl md:text-6xl leading-snug mx-auto font-semibold text-center w-[85%] md:w-[70%]">
          {heading}
        </h3>
        <p className="mt-5 w-[85%] md:w-[60%] mx-auto text-[18px] font-medium text-center">
          {paragraph || ""}
        </p>

        {showSwitch && (
          <div className="w-full">
            <div className="max-w-screen-xl mx-auto px-4 flex  justify-center items-center space-x-4 md:pb-8 pt-8 pb-0">
              <div className="inline-flex justify-center items-center  gap-3">
                <span
                  className={`md:text-xl text-sm  ${
                    isMonthly
                      ? "bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 bg-clip-text text-transparent font-bold"
                      : "text-gray-500 font-semibold"
                  }`}
                >
                  Monthly
                </span>
                <button
                  onClick={changeTab}
                  className="relative w-12 h-8 bg-gray-300 rounded-full p-1 transition-colors duration-300"
                >
                  <motion.div
                    layout
                    className="w-6 h-6 bg-white rounded-full shadow-md"
                    animate={{ x: isMonthly ? 0 : 16 }}
                    transition={{
                      type: "spring",
                      stiffness: 400,
                      damping: 30,
                    }}
                  />
                </button>
                <span
                  className={`md:text-xl text-sm  ${
                    !isMonthly
                      ? "bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 bg-clip-text text-transparent font-bold"
                      : "text-gray-500 font-semibold"
                  }`}
                >
                  Annually{" "}
                  <span className="inline-flex justify-center items-center px-4 py-2  ml-2 bg-gray-100  rounded-md">
                    <span className="bg-purple/10  md:text-base text-xs bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 bg-clip-text text-transparent font-semibold">
                      Upto 40% Off
                    </span>
                  </span>
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className=" w-full max-w-screen-txl mx-auto flex flex-col px-4 lg:px-8 overflow-x-scroll lg:overflow-x-visible scrollbar-corner-white scrollbar-thin scrollbar-thumb-slate-500 scrollbar-track-slate-200">
        <div className="max-w-full w-[1024px] lg:w-full">
          <div
            className={`w-full lg:grid hidden grid-cols-5 lg:sticky top-20 gap-4 py-3 ${stickyMargin} bg-white`}
          >
            <div className="col-span-2 p-4 flex items-start justify-center text-4xl font-semibold flex-col">
              <h3 className="text-4xl font-semibold text-left">Features</h3>
              <p className="text-base text-gray-600 mt-2 max-w-2xl text-left">
                Compare what's included in each plan — from basic essentials to
                advanced tools for growing teams. Choose the right features for
                your needs.
              </p>
            </div>

            {pricingComparisonsData.plans.map((plan, index) => {
              return (
                <div
                  key={index}
                  className={`col-span-1 p-4 text-center flex flex-col gap-2 bg-surface rounded-xl`}
                >
                  <h2 className="text-2xl font-semibold">{plan.name}</h2>
                  <div className="flex justify-center items-end">
                    <p className="text-4xl font-bold">
                      <span className="text-2xl mr-1">{plan.price.symbol}</span>
                      {plan.price.amount}
                    </p>
                    <p className="text-xl">{plan.price.duration}</p>
                  </div>

                  <p className="text-center text-sm text-gray-600">
                    {isMonthly ? "Billed monthly" : "Billed Annually"}
                  </p>
                  <Link
                    href={``}
                    className={`border border-gray-800 hover:bg-gray-200 py-1 mt-4 rounded-md transition-all duration-500 ease-in-out `}
                  >
                    Subscribe
                  </Link>
                </div>
              );
            })}
          </div>
          {/* Mobile Tabs */}

          <div className="relative bg-white border-b  lg:hidden justify-between px-4 mb-4  w-full max-w-2xl mx-auto flex">
            {pricingComparisonsData.plans.map((plan, index) => {
              const isActive = index === activePlanIndex;
              return (
                <button
                  key={index}
                  onClick={() => setActivePlanIndex(index)}
                  className="relative flex flex-col items-center justify-center text-sm text-gray-600 hover:text-black transition-all px-2 py-3"
                >
                  <span
                    className={`${isActive ? "font-semibold" : "font-medium"}`}
                  >
                    {plan.name}
                  </span>

                  {isActive && (
                    <motion.div
                      layoutId="underline"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-600 rounded-full"
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 30,
                      }}
                    />
                  )}
                </button>
              );
            })}
          </div>
          <div className="w-full box-border">
            {pricingComparisonsData?.features?.map((feature, index) => {
              return (
                <>
                  <div
                    key={index}
                    className="w-full lg:grid hidden grid-cols-5 gap-x-2"
                  >
                    <div className={`col-span-5 p-4 text-xl font-medium`}>
                      {feature.category}
                    </div>
                    {feature.items.map((item, index) => {
                      return (
                        <div
                          key={index}
                          className="group col-span-5 grid grid-cols-5 gap-4 text-sm sm:text-lg border-b border-slate-200"
                        >
                          <div
                            className={`col-span-2 p-4 text-left  flex items-center group-hover:bg-surface-light/50 ${
                              item?.image
                                ? "underline underline-offset-2 decoration-dotted cursor-pointer"
                                : ""
                            }`}
                            onClick={() => setImagePopup(item?.image)}
                          >
                            {item.name}
                          </div>

                          {item.values.map((value, index) => {
                            return (
                              <div
                                key={index}
                                className={`col-span-1 p-4 text-center flex justify-center items-center bg-surface-light/50 group-hover:bg-surface-light`}
                              >
                                {value === true ? (
                                  <BsCheckCircleFill className="text-xl text-accent-purple" />
                                ) : value === false ? (
                                  ""
                                ) : (
                                  value
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                  {/* Mobile Single-Column Feature View */}
                  <div className="lg:hidden w-full">
                    <div className="text-xl font-medium px-4 py-2 text-left">
                      {feature.category}
                    </div>
                    {feature.items.map((item, itemIndex) => (
                      <div
                        key={itemIndex}
                        className="border-b border-slate-200 px-4 py-3 flex justify-between items-center"
                      >
                        <span
                          className={`text-sm ${
                            item?.image
                              ? "underline underline-offset-2 decoration-dotted cursor-pointer"
                              : ""
                          }`}
                          onClick={() =>
                            item?.image && setImagePopup(item.image)
                          }
                        >
                          {item.name}
                        </span>
                        <span className="text-sm text-right">
                          {item.values[activePlanIndex] === true ? (
                            <BsCheckCircleFill className="text-accent-purple text-lg" />
                          ) : item.values[activePlanIndex] === false ? (
                            ""
                          ) : (
                            item.values[activePlanIndex]
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              );
            })}
          </div>
        </div>
      </div>

      {imagePopup && (
        <FeatureInfoPopup
          image={imagePopup}
          close={() => setImagePopup(null)}
        />
      )}
    </div>
  );
};

export default PricingComparisons2;
